import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { FirTrueupTaxesService } from './fir-trueup-taxes.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
interface TrueUpRow {
  id: number;
  name: string;
  selectedValue: string;
  year: string;
  years: any[];
  currency: any;
  currencies: any[];
  principalCCYRate: string | number;
  selectedEntity: any;
  selectedEntityDisplay: string;
  isEdit: boolean;
  taxesAmount: string | number;
  TLCAmount: string | number;
  groupTransKey: string;
  transHeaderKey: string;
  transDetailsKey: string;
  adjInFC: string | number;
  adjInUSD: string | number;
  userUpdate: string;
  usrcomments: string;
  SEQUENCE_ID?: number;
  // Autocomplete properties
  entitySearchResults?: any[];
  showEntityDropdown?: boolean;
  entitySearchSubject?: Subject<string>;
  loadingEntities?: boolean;
  // Per-row saving state - separate flags for save and delete operations
  savingRow?: boolean;
  deletingRow?: boolean;
}

@Component({
    selector: 'gtw-fir-trueup-taxes',
    templateUrl: './fir-trueup-taxes.component.html',
    styleUrls: ['./fir-trueup-taxes.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FirTrueupTaxesComponent implements OnInit, OnDestroy {
  private static readonly bridgeHostSelector = 'gtw-fir-trueup-taxes-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  private entityLoadSeq = 0;
  private entitiesListSeq = 0;
  private lastRowInitKey = '';
  /** Bumped on row/modal reset / reinit so late HTTP responses do not mutate UI or show toasts. */
  private staleEpoch = 0;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;

  // Component state
  modalTitle = 'Add Entity';
  modalName = '';
  minimise = false;
  isInitialized = false;
  loadingEntities = false;
  
  // Data properties
  objects: TrueUpRow[] = [];
  filteredEntities: any[] = [];
  editRecords: any[] = [];
  editExistingEntity = true;
  
  // Global configuration
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};
  colData: any = {};
  gridData: any = {};
  logged_in_user = '';
  taxYear: string = '';
  scenario: string = '';
  jcd_key: number = 250;

  @Input('col-data')
  set _colData(colData: any) {
    try {
      if (typeof colData === 'string') {
        if (colData.includes('{{') && colData.includes('}}')) {
          return;
        }
        this.colData = safeJsonInput<any>(colData, null, 'fir-trueup-taxes.parse1');
      } else if (typeof colData === 'object') {
        this.colData = colData;
      }
    } catch(e) {
      console.log('Col data input not yet stable');
    }
  }

  @Input('grid-data')
  set _gridData(gridData: any) {
    try {
      if (typeof gridData === 'string') {
        if (gridData.includes('{{') && gridData.includes('}}')) {
          return;
        }
        this.gridData = safeJsonInput<any>(gridData, null, 'fir-trueup-taxes.parse2');
      } else if (typeof gridData === 'object') {
        this.gridData = gridData;
      }
    } catch(e) {
      console.log('Grid data input not yet stable');
    }
  }

  @Input('modal-name')
  set _modalName(modalName: any) {
    try {
      if (typeof modalName === 'string') {
        if (modalName.includes('{{') && modalName.includes('}}')) {
          console.log('Modal name contains template syntax, skipping:', modalName);
          return;
        }
        
        // If modalName is changing, reset initialization state
        const isModalNameChanging = this.modalName !== undefined && this.modalName !== modalName;
        if (isModalNameChanging) {
          this.entityLoadSeq++;
          this.entitiesListSeq++;
          this.staleEpoch++;
          this.isInitialized = false;
          this.objects = [];
          this.editRecords = [];
          this.editExistingEntity = true; // Reset to default
        }
        
        this.modalName = modalName;
      }
    } catch(e) {
      console.log('Modal name input not yet stable');
    }
  }

  constructor(
    private firTrueupTaxesService: FirTrueupTaxesService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.resetInitIfRowIdentityChanged();
    this.logged_in_user = this.globalService?.userSettingsData?.user?.sso_id || (this.globalService as any)?.sso_id || '';
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    const u = this.baseURLs || {};
    const g = this.GENERAL_CONFIG || {};
    return String(u.api ?? g.api ?? g.base_url ?? '').trim();
  }

  private resetInitIfRowIdentityChanged(): void {
    if (this.modalName !== 'exisitng905Entity') {
      this.lastRowInitKey = '';
      return;
    }
    const key = `${this.rowData?.COMBINATION_KEY ?? ''}|${this.rowData?.TAX_YEAR ?? ''}|${this.rowData?.TRANS_HEADER_KEY ?? ''}|${this.rowData?.TRANS_DETAILS_KEY ?? ''}`;
    if (this.lastRowInitKey && key !== this.lastRowInitKey) {
      this.isInitialized = false;
      this.objects = [];
      this.editRecords = [];
      this.entityLoadSeq++;
      this.entitiesListSeq++;
      this.staleEpoch++;
    }
    this.lastRowInitKey = key;
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }

    const api = this.resolveApiBase();
    const needsRowData = this.modalName === 'exisitng905Entity';
    const gp = this.globalService?.globalParams;
    const hasGlobalParams = gp != null && typeof gp === 'object' && Object.keys(gp).length > 0;

    const hasRequiredInputs =
      !!api &&
      hasGlobalParams &&
      this.modalName !== undefined &&
      (!needsRowData || this.rowData?.COMBINATION_KEY);

    if (!hasRequiredInputs) {
      return;
    }

    this.isInitialized = true;

    this.taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    this.scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    this.jcd_key = this.globalService?.globalParams?.jcd_key || 250;

    this.initializeForm();
  }

  initializeForm() {
    
    this.modalTitle = 'Add Entity';
    this.editRecords = [];
    this.objects = [];

    if (this.modalName === 'exisitng905Entity') {
      this.editExistingEntity = false;
      this.loadExistingEntity();
    } else {
      const minRows = 3;
      for (let i = 0; i < minRows; i++) {
        this.addNew({});
      }
    }
  }

  loadExistingEntity() {
    const seq = ++this.entityLoadSeq;
    const api = this.resolveApiBase();
    this.firTrueupTaxesService.getTrueUpTaxes(
      api,
      this.rowData.TAX_YEAR,
      this.rowData.COMBINATION_KEY,
      this.rowData.TRANS_HEADER_KEY,
      this.rowData.TRANS_DETAILS_KEY,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe((response: any) => {
      if (seq !== this.entityLoadSeq) {
        return;
      }

      // Handle response - API returns { jsonObject: [...] } or array directly
      const data = Array.isArray(response)
        ? response
        : (response?.jsonObject ?? []);
      
      let minRows = 1;
      if (data && data.length > 0) {
        this.editRecords = data;
        for (let i = 0; i < data.length; i++) {
          const res = data[i];
          const newRow = this.addNew(
            res,
            res.US_WTD_AVG_RATE,
            res.FIR_CTX_TAXES,
            res.TLC_AMT,
            res.GROUP_TRANS_KEY,
            res.TRANS_HEADER_KEY,
            res.TRANS_DETAILS_KEY,
            res.COMMENTS,
            true,
            res.UPDATED_BY + ' ' + res.UPDATED_ON.substring(0, 10)
          );
          // Pass the actual row object from this.objects array, not the API response
          this.prepareEditRows(newRow, res.COMBINATION_KEY, res.CURR);
          minRows = minRows - 1;
        }
      }
      for (let i = 0; i < minRows; i++) {
        this.addNew({});
      }
    }, () => {
      if (seq !== this.entityLoadSeq) {
        return;
      }
      this.isInitialized = false;
    });
  }

  prepareEditRows(rowObj: TrueUpRow, combinationKey: any, currCode: string) {
    this.getEntities('', (filteredEntities: any[]) => {
      // Use loose comparison to handle string/number type mismatch (like AngularJS)
      const selectedEntity = _.find(filteredEntities, (entity: any) => {
        // Convert both to string for comparison to handle type mismatches
        return String(entity.COMBINATION_KEY) === String(combinationKey);
      });
      if (selectedEntity) {
        rowObj.selectedEntity = selectedEntity;
        rowObj.selectedEntityDisplay = selectedEntity.DISPLAY_VALUE || '';
      } else {
        console.warn('No matching entity found for COMBINATION_KEY:', combinationKey);
      }
      this.getCurrencies(rowObj, () => {
        // Use loose comparison for currency matching
        const selectedCurrency = _.find(rowObj.currencies, (curr: any) => {
          return String(curr.CURR_CODE) === String(currCode);
        });
        if (selectedCurrency) {
          rowObj.currency = selectedCurrency;
        }
        this.updateWeightedAverage(rowObj);
      });
    });
  }

  getEntities(val: string, callback?: (entities: any[]) => void) {
    this.loadingEntities = true;
    const seq = ++this.entitiesListSeq;
    const api = this.resolveApiBase();
    this.firTrueupTaxesService.getEntities(
      api,
      this.taxYear,
      this.scenario,
      this.jcd_key,
      val,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe((res: any) => {
      if (seq !== this.entitiesListSeq) {
        return;
      }
      this.loadingEntities = false;
      // Handle response - check for jsonObject property first
      this.filteredEntities = res?.jsonObject || res || [];
      if (callback) {
        callback(this.filteredEntities);
      }
    }, (error: any) => {
      if (seq !== this.entitiesListSeq) {
        return;
      }
      this.loadingEntities = false;
      console.error('Error loading entities:', error);
    });
  }

  // Handle entity search input - equivalent to uib-typeahead in AngularJS
  onEntitySearch(searchTerm: string, obj: TrueUpRow) {
    if (!searchTerm || searchTerm.length < 3) {
      obj.entitySearchResults = [];
      obj.showEntityDropdown = false;
      obj.loadingEntities = false;
      return;
    }

    obj.showEntityDropdown = true;
    
    // Initialize the search subject if not already done
    if (!obj.entitySearchSubject) {
      obj.entitySearchSubject = new Subject<string>();
      
      // Set up the search pipeline with debouncing (equivalent to typeahead-wait-ms)
      obj.entitySearchSubject.pipe(
        debounceTime(300), // Similar to typeahead-wait-ms
        distinctUntilChanged(),
        switchMap((term: string) => {
          obj.loadingEntities = true; // Set loading for this specific row
          // API Call 1: getEntities (action_code: xmgv96)
          return this.firTrueupTaxesService.getEntities(
            this.resolveApiBase(),
            this.taxYear,
            this.scenario,
            this.jcd_key,
            term,
            this.globalService,
            this.filterParams
          );
        }),
        takeUntil(this.unsubscribe$)
      ).subscribe((res: any) => {
        obj.loadingEntities = false; // Clear loading for this specific row
        obj.entitySearchResults = res?.jsonObject || res || [];
      }, (error: any) => {
        obj.loadingEntities = false; // Clear loading for this specific row
        console.error('Error searching entities:', error);
        obj.entitySearchResults = [];
      });
    }
    
    obj.entitySearchSubject.next(searchTerm);
  }

  // Select entity from autocomplete - equivalent to typeahead-on-select
  onSelectEntity(entity: any, obj: TrueUpRow) {
    obj.selectedEntity = entity;
    obj.selectedEntityDisplay = entity.DISPLAY_VALUE;
    obj.entitySearchResults = [];
    obj.showEntityDropdown = false;
    
    // Call setEntity (equivalent to typeahead-on-select)
    this.setEntity(entity, obj);
    
    // Call getCurrencies (equivalent to ng-change in AngularJS)
    // API Call 2: getCurrencies (action_code: 3ucwjb)
    this.getCurrencies(obj);
  }

  setEntity(item: any, selectedObject: any) {
    selectedObject.selectedEntity = item;
    selectedObject.selectedEntityDisplay = item?.DISPLAY_VALUE || '';
  }

  // Close dropdown when clicking outside
  closeEntityDropdown(obj: TrueUpRow) {
    setTimeout(() => {
      obj.showEntityDropdown = false;
    }, 200);
  }

  getCurrencies(selectedObject: any, callback?: () => void) {
    const ccySeq = ((selectedObject as any).__gtwCcySeq ?? 0) + 1;
    (selectedObject as any).__gtwCcySeq = ccySeq;
    this.firTrueupTaxesService.getCurrencies(
      this.resolveApiBase(),
      selectedObject.year || this.taxYear,
      this.globalService,
      this.filterParams
    ).pipe(takeUntil(this.unsubscribe$))
    .subscribe((res: any) => {
      if (ccySeq !== (selectedObject as any).__gtwCcySeq) {
        return;
      }
      const list = Array.isArray(res) ? res : (res?.jsonObject ?? []);
      selectedObject.currencies = list;
      if (callback) {
        callback();
      }
    }, () => {
      if (ccySeq !== (selectedObject as any).__gtwCcySeq) {
        return;
      }
    });
  }

  getFirTaxAmt(selectedObject: any) {
    if (this.filteredEntities.length && selectedObject.year && selectedObject.selectedEntity) {
      const combination_key = selectedObject.selectedEntity.COMBINATION_KEY;
      const calcSeq = ((selectedObject as any).__gtwFirCalcSeq ?? 0) + 1;
      (selectedObject as any).__gtwFirCalcSeq = calcSeq;
      const api = this.resolveApiBase();

      this.firTrueupTaxesService.getTLCAmount(
        api,
        selectedObject.year,
        combination_key,
        this.globalService,
        this.filterParams
      ).pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        if (calcSeq !== (selectedObject as any).__gtwFirCalcSeq) {
          return;
        }
        const txAmount = _.filter(res, { TAX_YEAR: selectedObject.year, COMBINATION_KEY: combination_key });
        if (txAmount.length) {
          selectedObject.TLCAmount = txAmount[0].ADJ_AMT_1;
        }
        this.updateWeightedAverage(selectedObject);
      }, () => {
        if (calcSeq !== (selectedObject as any).__gtwFirCalcSeq) {
          return;
        }
      });

      this.firTrueupTaxesService.getFirTaxAmt(
        api,
        selectedObject.year,
        combination_key,
        this.globalService,
        this.filterParams
      ).pipe(takeUntil(this.unsubscribe$))
      .subscribe((res: any) => {
        if (calcSeq !== (selectedObject as any).__gtwFirCalcSeq) {
          return;
        }
        const txAmount = _.filter(res, { TAX_YEAR: selectedObject.year, COMBINATION_KEY: combination_key });
        if (txAmount.length) {
          selectedObject.taxesAmount = txAmount[0].PER_FIR_CTX_TAXES;
        }
        this.updateWeightedAverage(selectedObject);
      }, () => {
        if (calcSeq !== (selectedObject as any).__gtwFirCalcSeq) {
          return;
        }
      });
    }
  }

  updateWeightedAverage(obj: any) {
    obj.principalCCYRate = obj.US_WTD_AVG_RATE || obj.principalCCYRate;
    this.calcTaxAmount(obj);
  }

  calcTaxAmount(obj: any) {
    const taxes = Number(obj.taxesAmount);
    const tlc = Number(obj.TLCAmount);
    const rate = Number(obj.principalCCYRate);

    if (isNaN(taxes) || isNaN(tlc) || isNaN(rate) ||
        obj.taxesAmount == null || obj.TLCAmount == null || obj.principalCCYRate == null) {
      obj.adjInFC = '';
      obj.adjInUSD = '';
      return;
    }

    if (tlc === 0) {
      obj.adjInFC = 0;
      obj.adjInUSD = 0;
    } else {
      obj.adjInFC = tlc - taxes;
      obj.adjInUSD = rate * obj.adjInFC;
    }
  }

  addNew(
    row: any = {},
    principalCCYRate: any = '',
    taxesAmount: any = '',
    TLCAmount: any = '',
    groupTransKey: any = '',
    transHeaderKey: any = '',
    transDetailsKey: any = '',
    usrcomments: any = '',
    isEdit: boolean = false,
    userUpdate: any = ''
  ) {
    if (this.modalName === 'open905cAnalysisBox') {
      row = {};
    }
    
    const newRow: TrueUpRow = {
      id: this.objects.length + 1,
      name: 'PL' + (this.objects.length + 1),
      selectedValue: '',
      year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      years: [],
      currency: '',
      currencies: [],
      principalCCYRate: principalCCYRate || '',
      selectedEntity: { DISPLAY_VALUE: '' },
      selectedEntityDisplay: '',
      isEdit: isEdit || false,
      taxesAmount: taxesAmount || '',
      TLCAmount: TLCAmount || '',
      groupTransKey: groupTransKey || '',
      transHeaderKey: transHeaderKey || '',
      transDetailsKey: transDetailsKey || '',
      adjInFC: '',
      adjInUSD: '',
      userUpdate: userUpdate || '',
      usrcomments: usrcomments || '',
      // Initialize autocomplete properties
      entitySearchResults: [],
      showEntityDropdown: false,
      entitySearchSubject: undefined,
      loadingEntities: false,
      // Per-row saving state - separate flags for save and delete operations
      savingRow: false,
      deletingRow: false
    };
    
    // Copy additional properties from existing row
    if (row.SEQUENCE_ID) {
      newRow.SEQUENCE_ID = row.SEQUENCE_ID;
    }
    
    this.objects.push(newRow);
    return newRow;
  }

  singleRowSave(obj: TrueUpRow, isDelete: boolean = false) {
    if (!obj.selectedEntity || !obj.selectedEntity.COMBINATION_KEY || !obj.currency || !obj.currency.CURR_CODE) {
      this.gtwSnackbarService.openSnackBar('Please fill all required fields', 'gtw-snackbar--yellow');
      return;
    }

    // CRITICAL: COMBINATION_KEYS must come from the selected entity in the form row
    // This matches AngularJS: requestPayload.COMBINATION_KEYS = obj.selectedEntity.COMBINATION_KEY
    const combination_key = obj.selectedEntity.COMBINATION_KEY;
    
    const requestPayload: any = {
      LEID: obj.selectedEntity.LEID,
      CDRNO: obj.selectedEntity.CDR_NO,
      REPORTING_PERIOD: obj.selectedEntity.REPORTING_PERIOD,
      COMBINATION_KEYS: combination_key,  // Used in jsonSettings.combination_keys
      TAX_YEAR: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      GROUP_TRANS_KEY: obj.groupTransKey,
      TRANS_HEADER_KEY: obj.transHeaderKey,
      TRANS_DETAILS_KEY: obj.transDetailsKey,
      COMBINATION_KEY: combination_key,
      CHART_KEY: obj.selectedEntity.CHART_KEY,
      CURRENCY: obj.currency.CURR_CODE,
      EXCHANGE_RATE: obj.principalCCYRate,
      TLC_AMOUNT: obj.TLCAmount,
      TAXES_AMOUNT: obj.taxesAmount,
      COMMENTS: obj.usrcomments
    };

    if (obj.SEQUENCE_ID) {
      requestPayload.SEQUENCE_ID = obj.SEQUENCE_ID;
    }

    const checkLockObj = [{
      combination_key: obj.selectedEntity.COMBINATION_KEY
    }];

    const LockObj = {
      modal_name: this.modalName,
      lockingObj: checkLockObj
    };

    // Debug logging for checkLockedbj
    // Set saving/deleting state on the specific row - spinner shows only on clicked button
    if (isDelete) {
      obj.deletingRow = true;
    } else {
      obj.savingRow = true;
    }

    const saveEpoch = this.staleEpoch;

    const api = this.resolveApiBase();
    if (!api) {
      obj.savingRow = false;
      obj.deletingRow = false;
      this.gtwSnackbarService.openSnackBar('Unable to resolve API base', 'gtw-snackbar--red');
      return;
    }
    
    this.firTrueupTaxesService.save(
      api,
      requestPayload,
      isDelete,
      LockObj,
      this.globalService,
      this.filterParams
    ).pipe(
      takeUntil(this.unsubscribe$),
      finalize(() => {
        obj.savingRow = false;
        obj.deletingRow = false;
      })
    )
    .subscribe((res: any) => {
      if (saveEpoch !== this.staleEpoch) {
        return;
      }

      if (res.callSuccess === '1') {
        this.gtwSnackbarService.openSnackBar(
          'Data ' + (isDelete ? 'deleted' : 'saved') + ' Successfully.',
          'gtw-snackbar--green'
        );
        
        if (isDelete && obj.SEQUENCE_ID) {
          const index = _.findIndex(this.objects, { SEQUENCE_ID: obj.SEQUENCE_ID });
          if (index !== -1) {
            this.objects.splice(index, 1);
          }
        }

        this.notifyGridUpdate('refresh');

        if (this.editExistingEntity === false) {
          this.cancel();
        }
      } else {
        this.gtwSnackbarService.openSnackBar(
          'An error has occurred. If this error continues please go to the help page and contact support',
          'gtw-snackbar--red'
        );
      }
    }, (error: any) => {
      if (saveEpoch !== this.staleEpoch) {
        return;
      }
      this.gtwSnackbarService.openSnackBar('An error occurred while saving', 'gtw-snackbar--red');
    });
  }

  private notifyGridUpdate(detail: 'refresh' | 'cancel'): void {
    this.requestGridUpdate.emit(detail);
    const host = document.querySelector(FirTrueupTaxesComponent.bridgeHostSelector);
    host?.dispatchEvent(new CustomEvent('request-grid-update', { detail, bubbles: true }));
  }

  cancel() {
    this.notifyGridUpdate('cancel');
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  reinitialize() {
    this.entityLoadSeq++;
    this.staleEpoch++;
    this.isInitialized = false;
    this.tryInit();
  }
}

